import React, { useMemo, useState } from "react";
import styled from "styled-components";
import {
  useListContributorsQuery,
  useUpdateContributorMutation,
  type ContributorRecord,
} from "../../services/rtkApi/artistsGalleryApi";
import { listArtistThemes } from "../../features/artistsGallery/themes";

const ContributorsAdmin: React.FC = () => {
  const { data: users = [], isLoading, error } = useListContributorsQuery();
  const [updateContributor] = useUpdateContributorMutation();
  const [filter, setFilter] = useState("");

  const themes = useMemo(() => listArtistThemes(), []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.username.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.role?.toLowerCase().includes(q)
    );
  }, [filter, users]);

  // contributors first, then everyone else
  const sorted = useMemo(
    () =>
      [...filtered].sort((a, b) => {
        if (a.isContributor !== b.isContributor) {
          return a.isContributor ? -1 : 1;
        }
        return a.username.localeCompare(b.username);
      }),
    [filtered]
  );

  return (
    <>
      <style>{`
        @media (max-width: 700px) {
          .cadm2-page { padding: 12px 8px !important; }
          .cadm2-row { flex-wrap: wrap !important; gap: 8px !important; }
          .cadm2-input-full { width: 100% !important; max-width: 100% !important; box-sizing: border-box !important; }
          .cadm2-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; margin: 0 -8px; }
          .cadm2-grid-1 { grid-template-columns: 1fr !important; }
          .cadm2-card-grid { grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)) !important; }
        }
      `}</style>
      <Page className="cadm2-page">
        <Header>
          <h1>Contributors</h1>
          <p>
            Toggle the <code>contributor</code> role to let a user post in the
            artists gallery. Pick a card theme to control how their pieces render.
          </p>
          <FilterInput
            className="cadm2-input-full"
            type="search"
            placeholder="filter by username / email / role…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Header>

        {isLoading && <p>Loading…</p>}
        {error && <Err>Failed to load users (admin only).</Err>}

        <div className="cadm2-table-wrap">
          <Table>
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                <th>Contributor</th>
                <th>Theme</th>
                <th>Display name</th>
                <th>Bio</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((u) => (
                <ContributorRow
                  key={u._id}
                  user={u}
                  themes={themes}
                  onSave={(payload) =>
                    updateContributor({ username: u.username, payload })
                  }
                />
              ))}
            </tbody>
          </Table>
        </div>
      </Page>
    </>
  );
};

interface RowProps {
  user: ContributorRecord;
  themes: string[];
  onSave: (payload: {
    isContributor?: boolean;
    theme?: string;
    displayName?: string;
    bio?: string;
  }) => unknown;
}

const ContributorRow: React.FC<RowProps> = ({ user, themes, onSave }) => {
  const [theme, setTheme] = useState(user.artistProfile.theme);
  const [displayName, setDisplayName] = useState(user.artistProfile.displayName);
  const [bio, setBio] = useState(user.artistProfile.bio);

  const dirty =
    theme !== user.artistProfile.theme ||
    displayName !== user.artistProfile.displayName ||
    bio !== user.artistProfile.bio;

  return (
    <tr>
      <td>
        {user.userEmoji ? `${user.userEmoji} ` : ""}
        {user.username}
      </td>
      <td>{user.email}</td>
      <td>
        <code>{user.role}</code>
      </td>
      <td>
        <input
          type="checkbox"
          checked={user.isContributor}
          onChange={(e) => onSave({ isContributor: e.target.checked })}
        />
      </td>
      <td>
        <select
          className="cadm2-input-full"
          value={theme}
          onChange={(e) => setTheme(e.target.value)}
        >
          {themes.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </td>
      <td>
        <input
          className="cadm2-input-full"
          type="text"
          value={displayName}
          placeholder={user.username}
          onChange={(e) => setDisplayName(e.target.value)}
        />
      </td>
      <td>
        <input
          className="cadm2-input-full"
          type="text"
          value={bio}
          placeholder="(optional)"
          onChange={(e) => setBio(e.target.value)}
        />
      </td>
      <td>
        <SaveBtn
          disabled={!dirty}
          onClick={() => onSave({ theme, displayName, bio })}
        >
          save
        </SaveBtn>
      </td>
    </tr>
  );
};

const Page = styled.div`
  background: #c9febe;
  min-height: 100vh;
  padding: 24px;
`;

const Header = styled.div`
  margin-bottom: 1rem;

  h1 {
    margin: 0 0 0.25rem 0;
  }

  p {
    margin: 0 0 0.75rem 0;
    font-size: 0.9rem;
    opacity: 0.8;
  }

  code {
    background: rgba(0, 0, 0, 0.08);
    padding: 1px 6px;
    border-radius: 4px;
  }
`;

const FilterInput = styled.input`
  padding: 6px 10px;
  font-size: 0.95rem;
  border: 1px solid #aaa;
  border-radius: 6px;
  width: 100%;
  max-width: 320px;
  background: #fff;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  font-size: 0.85rem;

  th,
  td {
    padding: 8px 10px;
    border-bottom: 1px solid #eee;
    text-align: left;
    vertical-align: middle;
  }

  th {
    background: #f7f7f7;
    font-weight: 700;
  }

  input[type="text"],
  select {
    padding: 4px 6px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 0.85rem;
    width: 100%;
    box-sizing: border-box;
  }
`;

const SaveBtn = styled.button`
  padding: 4px 10px;
  font-size: 0.8rem;
  font-weight: 600;
  background: #221c14;
  color: #fff;
  border: 0;
  border-radius: 4px;
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const Err = styled.p`
  background: #ffecec;
  border: 1px solid #e8a0a0;
  color: #7a1f1f;
  padding: 12px 16px;
  border-radius: 8px;
`;

export default ContributorsAdmin;
